import React from "react";
import { Card, Button, Form, Space } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import VariationNameInput from "./VariationNameInput";
import VariationOption from "./VariationOption";
import { Variation } from "../types";
import { CONSTANTS } from "../constants";

interface ProductVariationsProps {
  variations: Variation[];
  onAddVariation: () => void;
  onRemoveVariation: (key: number) => void;
  onVariationNameChange: (key: number, value: string) => void;
  onOptionChange: (
    variationKey: number,
    optionKey: number,
    value: string
  ) => void;
  onOptionKeyPress: (
    variationKey: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => void;
  onRemoveOption: (variationKey: number, optionKey: number) => void;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, optionKey: string) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>, targetOptionKey: string) => void;
}

const ProductVariations: React.FC<ProductVariationsProps> = React.memo(
  ({
    variations,
    onAddVariation,
    onRemoveVariation,
    onVariationNameChange,
    onOptionChange,
    onOptionKeyPress,
    onRemoveOption,
    onDragStart,
    onDragOver,
    onDrop,
  }) => {
    return (
      <Card title="Product Variations">
        <Space direction="vertical" style={{ width: "100%" }}>
          {variations.map((variation, index) => (
            <Card
              key={variation.key}
              type="inner"
              title={`Variation ${index + 1}`}
              extra={
                <Button
                  type="link"
                  onClick={() => onRemoveVariation(variation.key)}
                >
                  Delete
                </Button>
              }
            >
              <Form.Item
                label="Variation Group Name"
                required
                help={!variation.name && "Please enter variation group name"}
              >
                <VariationNameInput
                  placeholder="E.g.: Color, Size..."
                  value={variation.name}
                  onChange={(value) =>
                    onVariationNameChange(variation.key, value)
                  }
                  pageId={83}
                />
              </Form.Item>
              <Form.Item label="Options" required>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {variation.options.map((option) => (
                    <div
                      key={option.id}
                      style={{
                        display: "flex",
                        gap: "8px",
                        flexDirection: "column",
                      }}
                      draggable={true}
                      onDragStart={(e) => onDragStart(e, option.id.toString())}
                      onDragOver={onDragOver}
                      onDrop={(e) => onDrop(e, option.id.toString())}
                    >
                      <VariationOption
                        option={option}
                        onDelete={() =>
                          onRemoveOption(variation.key, option.id)
                        }
                        onChange={(e: any) => {
                          onOptionChange(
                            variation.key,
                            option.id,
                            e.target.value
                          );
                        }}
                        onKeyPress={(e: any) => {
                          onOptionKeyPress(variation.key, e);
                        }}
                        placeholder="Enter option"
                        data-option-id={option.id}
                        status={option.error ? "error" : undefined}
                      />
                      {option.error && (
                        <div style={{ color: "#ff4d4f", fontSize: "12px" }}>
                          {option.error}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Form.Item>
              <div
                style={{ color: "#999", fontSize: "12px", marginTop: "-20px" }}
              >
                {variation.options.length}/{CONSTANTS.MAX_OPTIONS} options
              </div>
            </Card>
          ))}

          <Button
            type="dashed"
            onClick={onAddVariation}
            block
            icon={<PlusOutlined />}
          >
            Add Variation Group
          </Button>
        </Space>
      </Card>
    );
  }
);

export default ProductVariations;
